import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { API_BASE_URL } from '../config/config.js';

import {
  Box, Paper, Typography, Grid, Button, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Alert, CircularProgress, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete, MenuItem, Select, FormControl, InputLabel,
  Tabs, Tab, Divider, InputAdornment, Card, CardContent,
  CardActions
} from '@mui/material';
import {
  Add, Delete, Refresh, CloudUpload, Search, Clear,
  Flight, AirplanemodeActive, Sort, FilterList, FileDownload, Block,
  Image as ImageIcon, HideImage, Replay
} from '@mui/icons-material';

const AxiosAPI = axios.create({ baseURL: API_BASE_URL });

// Logos: the server accepts PNG, JPEG and WebP. nginx rejects request bodies over
// 1 MB, so large images are scaled down in the browser before they are uploaded.
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_LOGO_BYTES = 900 * 1024;
const MAX_LOGO_DIMENSION = 400; // boards show logos at row height

// Airlines the daily run subscribes per flight number; they get no airline-wide backfill
const PER_FLIGHT_AIRLINES = ['P4', 'VK'];

const BACKFILL_STATUS = {
  queued: { label: 'Queued', color: 'default' },
  running: { label: 'Subscribing…', color: 'info' },
  completed: { label: 'Subscribed', color: 'success' },
  partial: { label: 'Partly subscribed', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
  interrupted: { label: 'Interrupted', color: 'warning' },
  not_applicable: { label: 'Per-flight', color: 'default' }
};

const logoUrl = (code, version) =>
  `${API_BASE_URL}/api/airlines/logo/${encodeURIComponent(code)}?v=${version}`;

const logoErrorMessage = (error) => (
  error.response?.status === 413
    ? 'Logo is too large for the server (limit 1 MB)'
    : error.response?.data?.message || error.message || 'Failed to upload logo'
);

// "queued"/"running" that this server no longer tracks means the backend restarted mid-run
const backfillStateOf = (airline) => {
  const status = airline.subscriptionBackfill?.status;
  if ((status === 'queued' || status === 'running') && !airline.backfillActive) return 'interrupted';
  return status || null;
};

// Validate a chosen logo and scale it down when needed, so uploads stay under the limit
const prepareLogoFile = (file) => new Promise((resolve, reject) => {
  if (!file) {
    reject(new Error('No file selected'));
    return;
  }
  if (!LOGO_TYPES.includes(file.type)) {
    reject(new Error('Logo must be a PNG, JPEG or WebP image'));
    return;
  }

  const objectUrl = URL.createObjectURL(file);
  const img = new window.Image();

  img.onload = () => {
    URL.revokeObjectURL(objectUrl);
    const scale = Math.min(1, MAX_LOGO_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    if (scale === 1 && file.size <= MAX_LOGO_BYTES) {
      resolve(file);
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);

    // JPEG stays JPEG; PNG and WebP become PNG to keep transparency
    const type = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not process the image'));
      } else if (blob.size > MAX_LOGO_BYTES) {
        reject(new Error('Logo is still larger than 900 KB after resizing'));
      } else {
        const name = `${file.name.replace(/\.[^.]+$/, '')}.${type === 'image/jpeg' ? 'jpg' : 'png'}`;
        resolve(new File([blob], name, { type }));
      }
    }, type, 0.92);
  };

  img.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('The file is not a readable image'));
  };

  img.src = objectUrl;
});

const ManageAirlinesEnhanced = () => {
  // Auth - Get token from localStorage since Redux store may not have it
  const getAuthToken = () => {
    try {
      const authData = localStorage.getItem('auth'); // Correct key is 'auth', not 'authData'
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.token;
      }
    } catch (error) {
      console.error('Error reading auth token:', error);
    }
    return null;
  };

  const authToken = getAuthToken();

  // Airlines state
  const [airlines, setAirlines] = useState([]);
  const [loadingAirlines, setLoadingAirlines] = useState(false);
  const [newAirlineCode, setNewAirlineCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [airlineSort, setAirlineSort] = useState('code');

  // Logo state
  const [newAirlineLogo, setNewAirlineLogo] = useState(null); // { file, previewUrl }
  const [logoBusyCode, setLogoBusyCode] = useState(null);
  const [importingLogos, setImportingLogos] = useState(false);
  const newLogoInputRef = useRef(null);
  const rowLogoInputRef = useRef(null);
  const rowLogoTargetRef = useRef(null);

  // Flights state
  const [flights, setFlights] = useState([]);
  const [loadingFlights, setLoadingFlights] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState('');
  const [newFlightNumber, setNewFlightNumber] = useState('');
  const [flightSearchQuery, setFlightSearchQuery] = useState('');

  // UI state
  const [currentTab, setCurrentTab] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, data: null });
  const [bulkDialog, setBulkDialog] = useState({ open: false, type: null });
  const [bulkData, setBulkData] = useState('');
  const [dryRunResults, setDryRunResults] = useState(null);

  // Airline reference search
  const [airlineReferences, setAirlineReferences] = useState([]);
  const [loadingReferences, setLoadingReferences] = useState(false);

  useEffect(() => {
    // Set Authorization header on mount
    if (authToken) {
      AxiosAPI.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      console.log('✅ Authorization header set for airline management:', authToken.substring(0, 20) + '...');
    } else {
      console.error('❌ No auth token found for airline management');
    }

    // Load data
    loadAirlines();
    loadFlights();
  }, []); // Run once on mount

  // Release the chosen logo's preview when it is replaced or the page closes
  useEffect(() => () => {
    if (newAirlineLogo?.previewUrl) URL.revokeObjectURL(newAirlineLogo.previewUrl);
  }, [newAirlineLogo]);

  // Refresh subscription status while a backfill is queued or running on the server
  const backfillPending = airlines.some(airline => ['queued', 'running'].includes(backfillStateOf(airline)));
  useEffect(() => {
    if (!backfillPending) return undefined;
    const timer = setInterval(() => loadAirlines({ silent: true }), 10000);
    return () => clearInterval(timer);
  }, [backfillPending]);

  // =============================================================================
  // AIRLINE OPERATIONS
  // =============================================================================

  // silent: refresh without the loading spinner or error toasts (used for polling)
  const loadAirlines = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingAirlines(true);
      const response = await AxiosAPI.get('/api/admin/airlines/enhanced', {
        params: {
          search: searchQuery,
          sort: airlineSort
        }
      });

      if (response.data.success) {
        setAirlines(response.data.data);
      } else if (!silent) {
        toast.error('Failed to load airlines');
      }
    } catch (error) {
      console.error('Error loading airlines:', error);
      if (!silent) toast.error(error.response?.data?.message || 'Failed to load airlines');
    } finally {
      if (!silent) setLoadingAirlines(false);
    }
  };

  const searchAirlineReferences = async (query) => {
    try {
      setLoadingReferences(true);
      const response = await AxiosAPI.get('/api/admin/airlines/enhanced/search', {
        params: { query }
      });

      if (response.data.success) {
        setAirlineReferences(response.data.data);
      }
    } catch (error) {
      console.error('Error searching airline references:', error);
    } finally {
      setLoadingReferences(false);
    }
  };

  const uploadLogo = async (code, file) => {
    const prepared = await prepareLogoFile(file);
    const formData = new FormData();
    formData.append('logo', prepared);
    const response = await AxiosAPI.post(
      `/api/admin/airlines/enhanced/${encodeURIComponent(code)}/logo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  };

  const selectNewAirlineLogo = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow choosing the same file again
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error('Logo must be a PNG, JPEG or WebP image');
      return;
    }
    setNewAirlineLogo({ file, previewUrl: URL.createObjectURL(file) });
  };

  const addAirline = async () => {
    if (!newAirlineCode.trim()) {
      toast.error('Please enter an airline code');
      return;
    }

    const code = newAirlineCode.trim().toUpperCase();

    try {
      setLoadingAirlines(true);
      const response = await AxiosAPI.post('/api/admin/airlines/enhanced', {
        code
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setNewAirlineCode('');

        if (newAirlineLogo) {
          try {
            await uploadLogo(code, newAirlineLogo.file);
            toast.success(`Logo saved for ${code}`);
          } catch (logoError) {
            console.error('Error uploading logo:', logoError);
            toast.error(`${code} was added, but its logo was not saved: ${logoErrorMessage(logoError)}`);
          }
          setNewAirlineLogo(null);
        }

        loadAirlines();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error adding airline:', error);
      toast.error(error.response?.data?.message || 'Failed to add airline');
    } finally {
      setLoadingAirlines(false);
    }
  };

  const chooseRowLogo = (code) => {
    rowLogoTargetRef.current = code;
    rowLogoInputRef.current?.click();
  };

  const handleRowLogoSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const code = rowLogoTargetRef.current;
    rowLogoTargetRef.current = null;
    if (!file || !code) return;

    try {
      setLogoBusyCode(code);
      const result = await uploadLogo(code, file);
      toast.success(result.message || `Logo saved for ${code}`);
      loadAirlines({ silent: true });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(logoErrorMessage(error));
    } finally {
      setLogoBusyCode(null);
    }
  };

  const removeLogo = async (code) => {
    try {
      setLogoBusyCode(code);
      const response = await AxiosAPI.delete(`/api/admin/airlines/enhanced/${encodeURIComponent(code)}/logo`);
      toast.success(response.data.message);
      loadAirlines({ silent: true });
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error(error.response?.data?.message || 'Failed to remove logo');
    } finally {
      setLogoBusyCode(null);
    }
  };

  const importBuiltinLogos = async () => {
    try {
      setImportingLogos(true);
      const response = await AxiosAPI.post('/api/admin/airlines/enhanced/logos/import-builtin');
      const { imported, skipped, failed } = response.data.data;
      toast.success(
        `Imported ${imported.length} logo(s), ${skipped.length} already stored` +
        (failed.length ? `, ${failed.length} failed` : '')
      );
      if (failed.length) console.warn('Logo import failures:', failed);
      loadAirlines({ silent: true });
    } catch (error) {
      console.error('Error importing logos:', error);
      toast.error(error.response?.data?.message || 'Failed to import logos');
    } finally {
      setImportingLogos(false);
    }
  };

  const retrySubscriptions = async (code) => {
    try {
      const response = await AxiosAPI.post(`/api/admin/airlines/enhanced/${encodeURIComponent(code)}/subscriptions`);
      toast.success(response.data.message);
      loadAirlines({ silent: true });
    } catch (error) {
      console.error('Error starting subscriptions:', error);
      toast.error(error.response?.data?.message || 'Failed to start subscriptions');
    }
  };

  const removeAirline = async (code) => {
    try {
      setLoadingAirlines(true);
      const response = await AxiosAPI.delete(`/api/admin/airlines/enhanced/${code}`);

      if (response.data.success) {
        toast.success(response.data.message);
        loadAirlines();
        loadFlights();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error removing airline:', error);
      toast.error(error.response?.data?.message || 'Failed to remove airline');
    } finally {
      setLoadingAirlines(false);
    }
  };

  const bulkAddAirlines = async (dryRun = false) => {
    const lines = bulkData.split('\n').map(line => line.trim()).filter(line => line.length > 0);

    if (lines.length === 0) {
      toast.error('Please enter at least one airline code');
      return;
    }

    const airlineList = lines.map(code => ({ code }));

    try {
      setLoadingAirlines(true);
      const response = await AxiosAPI.post('/api/admin/airlines/enhanced/bulk', {
        airlines: airlineList,
        dryRun
      });

      if (response.data.success) {
        if (dryRun) {
          setDryRunResults(response.data.data);
          toast.success('Dry run completed. Review the results below.');
        } else {
          toast.success(response.data.message);
          setBulkDialog({ open: false, type: null });
          setBulkData('');
          setDryRunResults(null);
          loadAirlines();
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error bulk adding airlines:', error);
      toast.error(error.response?.data?.message || 'Failed to bulk add airlines');
    } finally {
      setLoadingAirlines(false);
    }
  };

  // =============================================================================
  // FLIGHT OPERATIONS
  // =============================================================================

  const loadFlights = async () => {
    try {
      setLoadingFlights(true);
      const response = await AxiosAPI.get('/api/admin/airlines/flights', {
        params: {
          search: flightSearchQuery
        }
      });

      if (response.data.success) {
        setFlights(response.data.data);
      } else {
        toast.error('Failed to load flights');
      }
    } catch (error) {
      console.error('Error loading flights:', error);
      toast.error(error.response?.data?.message || 'Failed to load flights');
    } finally {
      setLoadingFlights(false);
    }
  };

  const addFlight = async () => {
    if (!selectedAirline || !newFlightNumber.trim()) {
      toast.error('Please select an airline and enter a flight number');
      return;
    }

    try {
      setLoadingFlights(true);
      const response = await AxiosAPI.post('/api/admin/airlines/flights', {
        airlineCode: selectedAirline,
        flightNumber: newFlightNumber.trim()
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setNewFlightNumber('');
        setSelectedAirline('');
        loadFlights();
        loadAirlines(); // Refresh to update flight counts
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error adding flight:', error);
      toast.error(error.response?.data?.message || 'Failed to add flight');
    } finally {
      setLoadingFlights(false);
    }
  };

  const removeFlight = async (id) => {
    try {
      setLoadingFlights(true);
      const response = await AxiosAPI.delete(`/api/admin/airlines/flights/${id}`);

      if (response.data.success) {
        toast.success(response.data.message);
        loadFlights();
        loadAirlines(); // Refresh to update flight counts
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error removing flight:', error);
      toast.error(error.response?.data?.message || 'Failed to remove flight');
    } finally {
      setLoadingFlights(false);
    }
  };

  const toggleFlightBlock = async (id, currentlyBlocked) => {
    try {
      setLoadingFlights(true);
      const response = await AxiosAPI.patch(`/api/admin/airlines/flights/${id}/block`, {
        isBlocked: !currentlyBlocked
      });

      if (response.data.success) {
        toast.success(response.data.message);
        loadFlights();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error toggling flight block:', error);
      toast.error(error.response?.data?.message || 'Failed to toggle flight block');
    } finally {
      setLoadingFlights(false);
    }
  };

  const bulkAddFlights = async (dryRun = false) => {
    const lines = bulkData.split('\n').map(line => line.trim()).filter(line => line.length > 0);

    if (lines.length === 0) {
      toast.error('Please enter at least one flight');
      return;
    }

    // Parse CSV format: airlineCode,flightNumber
    const flightList = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      return {
        airlineCode: parts[0],
        flightNumber: parts[1]
      };
    });

    try {
      setLoadingFlights(true);
      const response = await AxiosAPI.post('/api/admin/airlines/flights/bulk', {
        flights: flightList,
        dryRun
      });

      if (response.data.success) {
        if (dryRun) {
          setDryRunResults(response.data.data);
          toast.success('Dry run completed. Review the results below.');
        } else {
          toast.success(response.data.message);
          setBulkDialog({ open: false, type: null });
          setBulkData('');
          setDryRunResults(null);
          loadFlights();
          loadAirlines();
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error bulk adding flights:', error);
      toast.error(error.response?.data?.message || 'Failed to bulk add flights');
    } finally {
      setLoadingFlights(false);
    }
  };

  // =============================================================================
  // UI HANDLERS
  // =============================================================================

  const handleConfirmAction = () => {
    const { action, data } = confirmDialog;
    setConfirmDialog({ open: false, action: null, data: null });

    if (action === 'removeAirline') {
      removeAirline(data);
    } else if (action === 'removeFlight') {
      removeFlight(data);
    } else if (action === 'removeLogo') {
      removeLogo(data);
    } else if (action === 'importLogos') {
      importBuiltinLogos();
    }
  };

  const handleBulkApply = () => {
    if (bulkDialog.type === 'airlines') {
      bulkAddAirlines(false);
    } else if (bulkDialog.type === 'flights') {
      bulkAddFlights(false);
    }
  };

  const handleDryRun = () => {
    if (bulkDialog.type === 'airlines') {
      bulkAddAirlines(true);
    } else if (bulkDialog.type === 'flights') {
      bulkAddFlights(true);
    }
  };

  const filteredAirlines = airlines.filter(airline =>
    !searchQuery ||
    airline.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (airline.name && airline.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const groupedFlights = flights.reduce((acc, flight) => {
    if (!acc[flight.airlineCode]) {
      acc[flight.airlineCode] = [];
    }
    acc[flight.airlineCode].push(flight);
    return acc;
  }, {});

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AirplanemodeActive />
        Enhanced Airline & Flight Management
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage airlines and flight numbers with advanced features including name resolution, bulk operations, and search.
      </Typography>

      <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={`Airlines (${airlines.length})`} />
        <Tab label={`Flight Numbers (${flights.length})`} />
      </Tabs>

      {/* ========================================================================= */}
      {/* TAB 1: AIRLINES */}
      {/* ========================================================================= */}

      {currentTab === 0 && (
        <>
          {/* Add New Airline */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Add New Airline
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  freeSolo
                  options={airlineReferences}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : `${option.code} - ${option.name}`
                  }
                  inputValue={newAirlineCode}
                  onInputChange={(e, value) => {
                    setNewAirlineCode(value.toUpperCase());
                    if (value.length >= 2) {
                      searchAirlineReferences(value);
                    }
                  }}
                  onChange={(e, value) => {
                    if (value && typeof value === 'object') {
                      setNewAirlineCode(value.code);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Airline Code"
                      placeholder="e.g., AA, P4, TK"
                      helperText="Start typing to search known airlines"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingReferences && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <input
                  ref={newLogoInputRef}
                  type="file"
                  accept={LOGO_TYPES.join(',')}
                  hidden
                  onChange={selectNewAirlineLogo}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {newAirlineLogo ? (
                    <>
                      <Box
                        component="img"
                        src={newAirlineLogo.previewUrl}
                        alt="Selected logo preview"
                        sx={{ height: 40, maxWidth: 100, objectFit: 'contain', backgroundColor: '#fff', p: 0.5, borderRadius: 1 }}
                      />
                      <Tooltip title="Remove selected logo">
                        <IconButton size="small" onClick={() => setNewAirlineLogo(null)}>
                          <Clear />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<ImageIcon />}
                      onClick={() => newLogoInputRef.current?.click()}
                    >
                      Choose Logo
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Optional · PNG, JPEG or WebP
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={addAirline}
                  disabled={loadingAirlines || !newAirlineCode.trim()}
                  startIcon={<Add />}
                >
                  Add Airline
                </Button>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              New airlines are subscribed with SITA for the next 14 days automatically, so their flights appear straight away.
            </Typography>
          </Paper>

          {/* Airlines List */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Current Airlines ({filteredAirlines.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  ref={rowLogoInputRef}
                  type="file"
                  accept={LOGO_TYPES.join(',')}
                  hidden
                  onChange={handleRowLogoSelected}
                />
                <Tooltip title="Copy the logos bundled with the app (Airline_Icons.json) into the database. Airlines that already have a logo are not changed.">
                  <span>
                    <Button
                      onClick={() => setConfirmDialog({ open: true, action: 'importLogos', data: null })}
                      startIcon={importingLogos ? <CircularProgress size={16} /> : <ImageIcon />}
                      size="small"
                      disabled={importingLogos}
                    >
                      Import Built-in Logos
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  onClick={() => setBulkDialog({ open: true, type: 'airlines' })}
                  startIcon={<CloudUpload />}
                  size="small"
                >
                  Bulk Upload
                </Button>
                <IconButton onClick={loadAirlines} disabled={loadingAirlines}>
                  <Refresh />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by code or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={airlineSort}
                      label="Sort By"
                      onChange={(e) => {
                        setAirlineSort(e.target.value);
                        setTimeout(loadAirlines, 100);
                      }}
                    >
                      <MenuItem value="code">Code</MenuItem>
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="createdAt">Recently Added</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {loadingAirlines ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : filteredAirlines.length === 0 ? (
              <Alert severity="info">
                No airlines found. Add airlines using the form above or bulk upload.
              </Alert>
            ) : (
              <TableContainer sx={{ backgroundColor: '#000' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#2a2a2a' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Code</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>Logo</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Airline Name</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>Flights</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>Subscriptions</TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAirlines.map((airline, index) => {
                      const perFlight = PER_FLIGHT_AIRLINES.includes(airline.code);
                      const backfillState = backfillStateOf(airline);
                      const backfill = airline.subscriptionBackfill || {};
                      const statusInfo = BACKFILL_STATUS[backfillState];
                      const backfillBusy = backfillState === 'queued' || backfillState === 'running';
                      const backfillDetails = [
                        backfill.from && backfill.to ? `${backfill.from} to ${backfill.to}` : null,
                        backfill.subscribed != null ? `${backfill.subscribed} subscribed` : null,
                        backfill.skipped ? `${backfill.skipped} already covered` : null,
                        backfill.failed ? `${backfill.failed} failed` : null,
                        backfill.error || null,
                        backfillState === 'interrupted' ? 'The server restarted before this finished — retry to complete it' : null
                      ].filter(Boolean).join(' · ');
                      const logoBusy = logoBusyCode === airline.code;

                      return (
                      <TableRow key={airline.code} hover sx={{ backgroundColor: index % 2 === 0 ? '#1a1a1a' : '#000', '&:hover': { backgroundColor: '#2a2a2a' } }}>
                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                          <Chip label={airline.code} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          {logoBusy ? (
                            <CircularProgress size={20} />
                          ) : airline.logoVersion ? (
                            <Box
                              component="img"
                              src={logoUrl(airline.code, airline.logoVersion)}
                              alt={`${airline.code} logo`}
                              sx={{ height: 36, maxWidth: 96, objectFit: 'contain', backgroundColor: '#fff', p: 0.5, borderRadius: 1, display: 'block', mx: 'auto' }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                              No logo
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                          {airline.name || (
                            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', fontWeight: 'bold' }}>
                              Unknown airline
                            </Typography>
                          )}
                          {airline.nameAutoResolved && (
                            <Chip label="Auto" size="small" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>
                          <Chip label={airline.flightCount || 0} size="small" />
                        </TableCell>
                        <TableCell align="center">
                          {perFlight ? (
                            <Tooltip title="Subscribed per flight number by the daily run">
                              <Chip label={BACKFILL_STATUS.not_applicable.label} size="small" variant="outlined" />
                            </Tooltip>
                          ) : statusInfo ? (
                            <Tooltip title={backfillDetails || statusInfo.label}>
                              <Chip
                                label={statusInfo.label}
                                color={statusInfo.color}
                                size="small"
                                icon={backfillBusy ? <CircularProgress size={12} color="inherit" /> : undefined}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                              Not run
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title={airline.logoVersion ? `Replace ${airline.code} logo` : `Upload ${airline.code} logo`}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={logoBusy}
                                onClick={() => chooseRowLogo(airline.code)}
                              >
                                <ImageIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {airline.logoVersion && (
                            <Tooltip title={`Remove ${airline.code} logo`}>
                              <span>
                                <IconButton
                                  size="small"
                                  sx={{ color: '#bbb' }}
                                  disabled={logoBusy}
                                  onClick={() => setConfirmDialog({
                                    open: true,
                                    action: 'removeLogo',
                                    data: airline.code
                                  })}
                                >
                                  <HideImage />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {!perFlight && (
                            <Tooltip title={backfillBusy ? 'Subscriptions in progress' : `Subscribe ${airline.code} for the next 14 days again`}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  disabled={backfillBusy}
                                  onClick={() => retrySubscriptions(airline.code)}
                                >
                                  <Replay />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={`Remove ${airline.code} and all its flights`}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDialog({
                                open: true,
                                action: 'removeAirline',
                                data: airline.code
                              })}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: FLIGHT NUMBERS */}
      {/* ========================================================================= */}

      {currentTab === 1 && (
        <>
          {/* Add New Flight */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Add New Flight Number
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Airline</InputLabel>
                  <Select
                    value={selectedAirline}
                    label="Airline"
                    onChange={(e) => setSelectedAirline(e.target.value)}
                  >
                    {airlines.map((airline) => (
                      <MenuItem key={airline.code} value={airline.code}>
                        {airline.code} - {airline.name || 'Unknown'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Flight Number"
                  value={newFlightNumber}
                  onChange={(e) => setNewFlightNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., 7582, 123"
                  helperText="Up to 8 alphanumeric characters"
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={addFlight}
                  disabled={loadingFlights || !selectedAirline || !newFlightNumber.trim()}
                  startIcon={<Add />}
                >
                  Add Flight
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Flights List */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Flight Numbers ({flights.length})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click a flight to block/unblock it from the boards. Blocked flights appear in red.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => setBulkDialog({ open: true, type: 'flights' })}
                  startIcon={<CloudUpload />}
                  size="small"
                >
                  Bulk Upload
                </Button>
                <IconButton onClick={loadFlights} disabled={loadingFlights}>
                  <Refresh />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search flights..."
                value={flightSearchQuery}
                onChange={(e) => {
                  setFlightSearchQuery(e.target.value);
                  setTimeout(loadFlights, 500);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: flightSearchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => {
                        setFlightSearchQuery('');
                        loadFlights();
                      }}>
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {loadingFlights ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : flights.length === 0 ? (
              <Alert severity="info">
                No flights found. Add flights using the form above or bulk upload.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(groupedFlights).sort(([a], [b]) => a.localeCompare(b)).map(([airlineCode, airlineFlights]) => {
                  const airline = airlines.find(a => a.code === airlineCode);
                  return (
                    <Card key={airlineCode} variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {airlineCode} - {airline?.name || 'Unknown'}
                          <Chip label={`${airlineFlights.length} flights`} size="small" sx={{ ml: 1 }} />
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {airlineFlights.sort((a, b) => a.flightNumber.localeCompare(b.flightNumber)).map((flight) => (
                            <Tooltip key={flight._id} title={flight.isBlocked ? 'Click to unblock' : 'Click to block from boards'}>
                              <Chip
                                label={flight.fullFlightNumber}
                                onClick={() => toggleFlightBlock(flight._id, flight.isBlocked)}
                                onDelete={() => setConfirmDialog({
                                  open: true,
                                  action: 'removeFlight',
                                  data: flight._id
                                })}
                                color={flight.isBlocked ? 'error' : 'primary'}
                                variant={flight.isBlocked ? 'filled' : 'outlined'}
                                icon={flight.isBlocked ? <Block /> : <Flight />}
                                sx={flight.isBlocked ? { textDecoration: 'line-through', opacity: 0.7 } : {}}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* ========================================================================= */}
      {/* BULK UPLOAD DIALOG */}
      {/* ========================================================================= */}

      <Dialog
        open={bulkDialog.open}
        onClose={() => {
          setBulkDialog({ open: false, type: null });
          setBulkData('');
          setDryRunResults(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bulk Upload {bulkDialog.type === 'airlines' ? 'Airlines' : 'Flights'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {bulkDialog.type === 'airlines' ? (
              <>Enter airline codes, one per line. Example:<br />P4<br />TK<br />ET</>
            ) : (
              <>Enter flights in CSV format: airlineCode,flightNumber (one per line). Example:<br />P4,7582<br />TK,123<br />ET,456</>
            )}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={bulkData}
            onChange={(e) => setBulkData(e.target.value)}
            placeholder={bulkDialog.type === 'airlines' ? 'P4\nTK\nET' : 'P4,7582\nTK,123\nET,456'}
          />

          {dryRunResults && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Dry Run Results</Typography>

              {dryRunResults.added.length > 0 && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  <strong>Will Add ({dryRunResults.added.length}):</strong>
                  <Box component="span" sx={{ ml: 1 }}>
                    {dryRunResults.added.map(item =>
                      bulkDialog.type === 'airlines'
                        ? `${item.code}${item.name ? ` (${item.name})` : ''}`
                        : item.fullFlightNumber
                    ).join(', ')}
                  </Box>
                </Alert>
              )}

              {dryRunResults.duplicates.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <strong>Duplicates ({dryRunResults.duplicates.length}):</strong>
                  <Box component="span" sx={{ ml: 1 }}>
                    {dryRunResults.duplicates.map(item =>
                      bulkDialog.type === 'airlines' ? item.code : item.fullFlightNumber
                    ).join(', ')}
                  </Box>
                </Alert>
              )}

              {dryRunResults.errors.length > 0 && (
                <Alert severity="error">
                  <strong>Errors ({dryRunResults.errors.length}):</strong>
                  <Box component="span" sx={{ ml: 1, display: 'block' }}>
                    {dryRunResults.errors.map((err, idx) => (
                      <div key={idx}>
                        {bulkDialog.type === 'airlines' ? err.code : `${err.airlineCode},${err.flightNumber}`}: {err.error}
                      </div>
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBulkDialog({ open: false, type: null });
            setBulkData('');
            setDryRunResults(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleDryRun} variant="outlined">
            Test (Dry Run)
          </Button>
          <Button
            onClick={handleBulkApply}
            variant="contained"
            disabled={loadingAirlines || loadingFlights}
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* CONFIRMATION DIALOG */}
      {/* ========================================================================= */}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, data: null })}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'removeAirline' &&
              `Are you sure you want to remove airline "${confirmDialog.data}" and all its associated flights?`}
            {confirmDialog.action === 'removeFlight' &&
              'Are you sure you want to remove this flight?'}
            {confirmDialog.action === 'removeLogo' &&
              `Remove the logo for ${confirmDialog.data}? Display boards will show this airline without a logo until a new one is uploaded.`}
            {confirmDialog.action === 'importLogos' &&
              'Copy all logos bundled with the app (Airline_Icons.json) into the database? Airlines that already have a logo are not changed. After this, the display boards use the logos stored here.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, data: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === 'importLogos' ? 'primary' : 'error'}
            variant="contained"
            disabled={loadingAirlines || loadingFlights}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageAirlinesEnhanced;
